import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import withRouter from '../../../components/withRouter';
import TableContainer from '../../../components/TableContainerReactTable';
import { getOrders } from '../services/orderApi';
import { AdType, Order } from '../types/order';
import { format } from 'date-fns';
import { useProfile } from '../../(identity)/hooks/UserHooks';
import ModalOrderTag from '../components/order/ModalOrderTag';
import { useNavigate } from 'react-router-dom';
import ModalOrderVast from '../components/order/ModalOrderVast';
import AddEditOrderModal from '../components/order/AddEditOrderModal';
import { Loader2 } from 'lucide-react';

export const getOrderStatusColor = (status: string) => {
    switch (status) {
        case 'PENDING_APPROVAL':
            return 'info';
        case 'DRAFT':
            return 'light text-body';
        case 'DISAPPROVED':
            return 'danger';
        case 'INACTIVE':
            return 'secondary';
        case 'READY':
            return 'info';
        case 'NEEDS_CREATIVES':
            return 'danger';
        case 'NEEDS_CLICK_TAG':
            return 'danger';
        case 'DELIVERING':
            return 'success';
        case 'DELIVERY_EXTENDED':
            return 'success';
        case 'PAUSED':
            return 'warning';
        case 'COMPLETED':
            return 'light text-body';
        case 'ARCHIVED':
            return 'light text-body';
        default:
            return 'secondary';
    }
};

const Index = (props: any) => {
    const navigate = useNavigate();
    const { userProfile } = useProfile();
    const [loading, setLoading] = useState<boolean>(false);
    const [orders, setOrders] = useState<Order[]>([]);
    const [modalOrderTag, setModalOrderTag] = useState<boolean>(false);
    const [modalOrderVast, setModalOrderVast] = useState<boolean>(false);
    const [modalAddEditOrder, setModalAddEditOrder] = useState<boolean>(false);
    const [dataToEdit, setDataToEdit] = useState<Order | null>(null);
    const numberFormat = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

    useEffect(() => {
        getOrders().then((res) => {
            setOrders(res);
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    const toggleModalOrderTag = () => {
        setModalOrderTag(!modalOrderTag);
    };

    const toggleModalOrderVast = () => {
        setModalOrderVast(!modalOrderVast);
    };

    const toggleModalAddEditOrder = () => {
        setModalAddEditOrder(!modalAddEditOrder);
        if (!modalAddEditOrder) {
            setDataToEdit(null);
        }
    };

    const handleOrderSaved = () => {
        getOrders().then((res) => {
            setOrders(res);
        });
    };

    const columns = [
        {
            header: 'ID',
            cell: (cell: any) => {
                return (
                    <span className="fw-semibold">{cell.getValue()}</span>
                );
            },
            accessorKey: 'id',
            enableColumnFilter: false,
            sortingFn: 'basic',
            sortDescFirst: true,
        },
        {
            header: 'Nome',
            accessorKey: 'campaign.name',
            enableColumnFilter: false,
        },
        {
            header: 'Advertiser',
            accessorKey: 'campaign.advertiser.name',
            enableColumnFilter: false,
        },
        {
            header: 'Agência',
            accessorKey: 'campaign.agency.name',
            enableColumnFilter: false,
        },
        {
            header: 'Publisher',
            accessorKey: 'publisher.name',
            enableColumnFilter: false,
        },
        {
            header: 'Tipo',
            accessorKey: 'adType',
            cell: (cell: any) => {
                const row = cell.row.original;
                const badges = [];

                switch (row.adType) {
                    case AdType.VIDEO:
                        badges.push(<span key="video" className="badge bg-danger">Vídeo</span>);
                        break;
                    case AdType.AUDIO:
                        badges.push(<span key="audio" className="badge bg-warning">Áudio</span>);
                        break;
                    case AdType.DISPLAY:
                        badges.push(<span key="display" className="badge bg-info">Display</span>);
                        break;
                    default:
                        break;
                }

                if (row.creative?.companionAd) {
                    badges.push(
                        <>
                            <span>+ </span>
                            <span key="companion" className="badge bg-secondary">Companion</span>
                        </>
                    );
                }

                if (Array.isArray(row.creative?.masterCreatives) && row.creative.masterCreatives.length > 0) {
                    badges.push(<span key="master" className="badge bg-primary">Master</span>);
                }

                return <div className="d-flex gap-2">{badges}</div>;
            },
            enableColumnFilter: false,
        },
        {
            header: 'Bloco de Anúncio',
            accessorKey: 'placement.name',
            enableColumnFilter: false,
        },
        {
            header: 'Tamanhos',
            accessorFn: (row: any) => {
                return row.sizes.join(', ');
            },
            enableColumnFilter: false,
        },
        {
            header: 'Status',
            accessorKey: 'status',
            cell: (cell: any) => {
                return cell.row.original.status.map((status: string) => {
                    let statusStr = status;
                    switch (status) {
                        case 'READY':
                            statusStr = 'Pronto';
                            break;
                        case 'NEEDS_CREATIVES':
                            statusStr = 'Criativos Necessários';
                            break;
                        case 'NEEDS_CLICK_TAG':
                            statusStr = 'Click Tag Necessário';
                            break;
                        case 'DELIVERING':
                            statusStr = 'Entregando';
                            break;
                        case 'DELIVERY_EXTENDED':
                            statusStr = 'Entrega Estendida';
                            break;
                        case 'PAUSED':
                            statusStr = 'Pausado';
                            break;
                        case 'COMPLETED':
                            statusStr = 'Completo';
                            break;
                        case 'ARCHIVED':
                            statusStr = 'Arquivado';
                            break;
                        case 'PENDING_APPROVAL':
                            statusStr = 'Pendente de Aprovação';
                            break;
                        case 'DRAFT':
                            statusStr = 'Rascunho';
                            break;
                        case 'DISAPPROVED':
                            statusStr = 'Reprovado';
                            break;
                        case 'INACTIVE':
                            statusStr = 'Inativo';
                            break;
                        default:
                            statusStr = 'Desconhecido';
                            break;
                    }
                    return (
                        <span
                            key={status}
                            className={`badge bg-${getOrderStatusColor(status)}`}
                        >
                            {statusStr}
                        </span>
                    );
                });
            },
            enableColumnFilter: false,
        },
        {
            header: 'Data de Início',
            accessorFn: (row: any) => {
                return format(new Date(row.startDate), 'dd/MM/yyyy');
            },
            enableColumnFilter: false,
        },
        {
            header: 'Data de Termino',
            accessorFn: (row: any) => {
                return format(new Date(row.endDate), 'dd/MM/yyyy');
            },
            enableColumnFilter: false,
        },
        {
            header: 'Impressões',
            cell: (cell: any) => {
                const row = cell.row.original;
                const formattedImpressions = numberFormat.format(row.impressions);
                const formattedDeliveredImpressions = numberFormat.format(row.deliveryImpressions);
                return `${formattedDeliveredImpressions} / ${formattedImpressions}`;
            },
            enableColumnFilter: false,
        },
        {
            id: 'actions',
            cell: (cell: any) => {
                const row = cell.row.original;
                const actionCell = [];

                if (AdType.AUDIO === row.adType || AdType.VIDEO === row.adType) {
                    actionCell.push(
                        <TooltipProvider key="action_vast">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        className="btn text-primary d-inline-block"
                                        onClick={() => {
                                            setDataToEdit(row);
                                            toggleModalOrderVast();
                                        }}
                                    >
                                        <i className="ri-code-s-slash-fill fs-16"></i>
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>VAST</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    );
                } else {
                    actionCell.push(
                        <TooltipProvider key="action_tag">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        className="btn text-primary d-inline-block"
                                        onClick={() => {
                                            setDataToEdit(row);
                                            toggleModalOrderTag();
                                        }}
                                    >
                                        <i className="ri-javascript-fill fs-16"></i>
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>TAG</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    );
                }

                return <div className="flex gap-2">{actionCell}</div>;
            },
            enableColumnFilter: false,
            enableSorting: false,
        },
    ];

    document.title = 'Pedidos | Ad Pulse';
    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <div className="grid gap-4">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <CardTitle>Pedidos</CardTitle>
                                    </div>
                                    <div>
                                        <Button
                                            onClick={toggleModalAddEditOrder}
                                        >
                                            <i className="ri-add-line align-middle mr-1"></i>
                                            Adicionar Pedido
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {!loading ? (
                                    <TableContainer
                                        divClass="table-responsive"
                                        columns={(columns || [])}
                                        data={(orders || [])}
                                        isGlobalFilter={true}
                                        customPageSize={15}
                                        SearchPlaceholder="Procurar..."
                                    />
                                ) : (
                                    <div className="flex justify-center">
                                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
            {modalOrderTag && dataToEdit && (
                <ModalOrderTag
                    order={dataToEdit}
                    isOpen={modalOrderTag}
                    toggle={toggleModalOrderTag}
                />
            )}

            {modalOrderVast && dataToEdit && (
                <ModalOrderVast
                    order={dataToEdit}
                    isOpen={modalOrderVast}
                    toggle={toggleModalOrderVast}
                />
            )}

            <AddEditOrderModal
                isOpen={modalAddEditOrder}
                toggle={toggleModalAddEditOrder}
                order={dataToEdit}
                onOrderSaved={handleOrderSaved}
            />
        </React.Fragment>
    );
};

export default withRouter(Index);
